import React, { useContext } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { makeStyles } from 'tss-react/mui'
import { Card, Typography, Button, Box, IconButton, useMediaQuery, useTheme } from '@mui/material'
import useConnectVehicles from '../../hooks/use-connect-vehicles'
import { ConnectVehicle } from '../../models'
import { Alert } from '@e3dc-react/shell/Elements/Alert'
import { ManufacturersContext } from '../../providers'
import { useRouting } from '../../hooks'
import { getTranslationBasedOnEhaErrorCodes, isE3dcError } from '../../functions/errors.functions'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { J1LeftarrowC } from '@e3dc-react/icons'
import ConnectVehicleTile from './ConnectVehiclesTile'
import useUserVehicles from '../../hooks/use-user-vehicles'
import { MANUFACTURERS_WITHOUT_VIN } from '../../consts'

const useStyles = makeStyles()(theme => ({
  vehicleCardButton: {
    display: 'flex',
    margin: `${theme.spacing(4)} auto ${theme.spacing(4)}`,
  },
  title: {
    textAlign: 'center',
    margin: theme.spacing(1),
  },
  header: {
    borderBottom: '1px solid',
    borderColor: theme.palette.shades.shade03,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  vehicle: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    margin: 'auto',
    textAlign: 'center',
  },
}))

interface Props {
  installationId?: string
  selectedVehiclesIds: string[]
  setSelectedVehiclesIds: React.Dispatch<React.SetStateAction<string[]>>
  vendorAccount?: string
  handleConnectVehicles: () => Promise<void>
}

/*
 * Shows a list of vehicles which can be connected or already are connected to EHA.
 */
const ConnectVehiclesList: React.FC<Props> = ({
  installationId,
  selectedVehiclesIds,
  setSelectedVehiclesIds,
  vendorAccount,
  handleConnectVehicles,
}) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { enqueueErrorStack } = useSnackbar()
  const manufacturersContext = useContext(ManufacturersContext)
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'))
  /* vehicles currently available for connection */
  const { vehicles, hasError, error } = useConnectVehicles(installationId, vendorAccount)
  /* vehicles already connected to EHA */
  const { vehicles: userVehicles } = useUserVehicles(Number(installationId))

  const { navigateToAddVehiclePage } = useRouting()

  if (error && isE3dcError(error)) {
    enqueueErrorStack(getTranslationBasedOnEhaErrorCodes(error.response?.data.errno))
  }

  const toggleItem = (vehicleId: string): void => {
    if (selectedVehiclesIds.some(vehicle => vehicle === vehicleId)) {
      setSelectedVehiclesIds(selectedVehiclesIds.filter(vehicle => vehicle !== vehicleId))
      return
    }
    setSelectedVehiclesIds([...selectedVehiclesIds, vehicleId])
  }

  const handleOnClick = (): void => {
    handleConnectVehicles()
  }

  const handleBackClick = (): void => {
    navigateToAddVehiclePage()
  }

  return (
    <>
      {hasError && (
        <Box sx={{ width: '100%', pb: 2 }}>
          <Alert severity="error" text={t('emobility.vehicle-integration.connect-vehicle-fetching-failed')} />
        </Box>
      )}
      <Card>
        <Box className={classes.header} p={2}>
          <Box sx={{ flex: 1 }}>
            <IconButton color="primary" aria-label="close" onClick={handleBackClick}>
              <J1LeftarrowC
                sx={{
                  width: theme => theme.spacing(5),
                  height: theme => theme.spacing(5),
                }}
              />
            </IconButton>
          </Box>
          <Typography className={classes.title} variant="h3">
            {t('emobility.connect-vehicles-dialog.title')}
          </Typography>
          <Box sx={{ flex: 1 }} />
        </Box>
        <Box className={classes.vehicle} p={2} width={isDesktop ? '75%' : '100%'} margin={'auto'}>
          <Typography variant="body2" p={2}>
            {t('emobility.connect-vehicles-dialog.description')}
          </Typography>
        </Box>
      </Card>
      <Typography sx={{ textAlign: 'center', mt: 2, mb: 2 }} variant="h4">
        {t('emobility.connect-vehicles-dialog.title-vehicles')}
      </Typography>
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(240px, 480px))',
            md: 'repeat(3, minmax(240px, 480px))',
          },

          justifyContent: { md: 'center' },
          gap: 2,
        }}
      >
        {vehicles.map((vehicle: ConnectVehicle) => {
          const logoUrl = manufacturersContext?.manufacturers.find(manufacturer => manufacturer.id === vehicle.manufacturer)?.logoUrl
          const isManufacturerWithoutVin = MANUFACTURERS_WITHOUT_VIN.includes(vehicle.manufacturer)

          /* check if the vehicle is already connected to eha */
          const isAlreadyConnected: boolean =
            userVehicles?.some(userVehicle => {
              /* BMW and MINI do not have vin. so we use externalId */
              if (isManufacturerWithoutVin) return userVehicle.externalId === vehicle.externalId
              return userVehicle.vin === vehicle.vin
            }) ?? false

          return (
            <ConnectVehicleTile
              key={vehicle.id}
              logoUrl={logoUrl}
              vehicleName={vehicle.name}
              vin={vehicle?.vin || ''}
              toggleItem={() => toggleItem(vehicle.id)}
              isSelected={selectedVehiclesIds.includes(vehicle.id)}
              isAlreadyConnected={isAlreadyConnected}
            />
          )
        })}
      </Box>
      {vehicles.length > 0 && (
        <Button variant="contained" className={classes.vehicleCardButton} onClick={handleOnClick} disabled={selectedVehiclesIds.length === 0}>
          {t('emobility.vehicle-integration.connect-vehicle')}
        </Button>
      )}
    </>
  )
}

export default ConnectVehiclesList
